import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { useState } from "react";
import { DownloadIcon } from "lucide-react";
import { toast } from "sonner";
import LoadingAnimation from "../../LoadingAnimation";

interface ExportButtonProps {
    onExport: () => Promise<any>;
    fileName: string;
    buttonLabel?: string;
    dialogTitle?: string;
    dialogDescription?: string;
    successTitle?: string;
    successDescription?: string;
    errorTitle?: string;
    errorDescription?: string;
    exportLabel?: string;
    variant?:
        | "default"
        | "destructive"
        | "outline"
        | "secondary"
        | "ghost"
        | "link";
    disabled?: boolean;
}

function ExportButton({
    onExport,
    buttonLabel,
    fileName = "latest-export",
    dialogTitle = "Export Data",
    dialogDescription = "Are you sure you want to export this data? Click export to confirm.",
    successTitle = "Export successful",
    successDescription = "The operation was completed successfully.",
    errorTitle = "Oops! Something went wrong",
    errorDescription = "An error occurred while exporting the data.",
    exportLabel = "Export",
    variant = "outline",
    disabled = false,
}: ExportButtonProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleExport = async () => {
        setIsLoading(true);
        try {
            const response = await onExport();

            const blob = new Blob([response.data], {
                type: response.headers["content-type"],
            });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${fileName}.xlsx`);
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(successTitle, {
                description: successDescription,
                className: "bg-green-300",
            });
        } catch (error) {
            toast(errorTitle, {
                description: errorDescription,
            });
            console.error("Export failed:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
            <DialogTrigger asChild>
                <Button
                    variant={variant}
                    onClick={() => setIsOpen(true)}
                    disabled={disabled || isLoading}
                >
                    {isLoading ? (
                        <LoadingAnimation />
                    ) : (
                        <>
                            <DownloadIcon />
                            {buttonLabel && (
                                <span className="ml-2">{buttonLabel}</span>
                            )}
                        </>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{dialogTitle}</DialogTitle>
                    <DialogDescription>{dialogDescription}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        type="submit"
                        onClick={() => {
                            setIsOpen(false);
                            handleExport();
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? "Exporting..." : exportLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ExportButton;
